import { COOKIE_KEY } from '@/lib/Constants';
import { getCookieForApp, getOsType, getTokenForApp, setCookieForApp } from '@/lib/CookieUtils';
import { Endpoints } from '@/lib/Endpoints';
import { sendCRODataLayerEvent } from '@/lib/gtm-wrapper';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { isAppClient } from '@/lib/LocalStorage';
import { safeSessionStorage } from '@/lib/safeStorage';

export const AB_KEYS = {
  checkout: 'checkout',
  checkoutOptional: 'checkout_optional',
} as const;

export type AbExperimentKey = (typeof AB_KEYS)[keyof typeof AB_KEYS];

export type AbExperimentMap = Record<string, string>;

const SEEN_PREFIX = 'hzc_ab_seen:';

let memoryMap: AbExperimentMap = {};

export function parseExperiments(raw: unknown): AbExperimentMap {
  if (!raw) {
    return {};
  }
  let source: unknown = raw;
  if (typeof raw === 'string') {
    try {
      source = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return {};
  }
  const map: AbExperimentMap = {};
  Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
    const experimentKey = String(key || '').trim().toLowerCase();
    const variant = String(value || '').trim().toLowerCase();
    if (experimentKey && variant) {
      map[experimentKey] = variant;
    }
  });
  return map;
}

export function serializeExperiments(map: AbExperimentMap): string {
  return JSON.stringify(map || {});
}

export function saveExperiments(map: AbExperimentMap, opts?: { identityChanged?: boolean }) {
  const next = parseExperiments(map);
  memoryMap = next;
  if (typeof window !== 'undefined') {
    setCookieForApp(COOKIE_KEY.AB, serializeExperiments(next));
    if (opts?.identityChanged) {
      clearSeenKeys();
    }
  }
  return next;
}

export function applyExperimentsFromPayload(data: { experiments?: unknown } | null | undefined) {
  if (!data) {
    return {};
  }
  return saveExperiments(parseExperiments(data.experiments), { identityChanged: true });
}

export function getExperiments(): AbExperimentMap {
  if (typeof window === 'undefined') {
    return { ...memoryMap };
  }
  const fromCookie = parseExperiments(getCookieForApp(COOKIE_KEY.AB));
  if (Object.keys(fromCookie).length > 0) {
    memoryMap = fromCookie;
    return { ...fromCookie };
  }
  return { ...memoryMap };
}

export function getVariant(experimentKey: string): string | undefined {
  const key = String(experimentKey || '').trim().toLowerCase();
  if (!key) {
    return undefined;
  }
  return getExperiments()[key];
}

export function isVariant(experimentKey: string, variant: string): boolean {
  const expected = String(variant || '').trim().toLowerCase();
  if (!expected) {
    return false;
  }
  return getVariant(experimentKey) === expected;
}

export function markExperimentSeen(experimentKey: string) {
  if (typeof window === 'undefined' || isAppClient()) {
    return;
  }
  const key = String(experimentKey || '').trim().toLowerCase();
  const variant = getVariant(key);
  if (!key || !variant) {
    return;
  }
  const seenKey = `${SEEN_PREFIX}${key}:${variant}`;
  if (safeSessionStorage.getItem(seenKey)) {
    return;
  }
  safeSessionStorage.setItem(seenKey, '1');

  sendCRODataLayerEvent({
    event: 'experiment_exposure',
    experiment_key: key,
    variant_key: variant,
  });

  const token = getTokenForApp();
  if (!token) {
    return;
  }

  const osType = (getOsType() || 'web').toLowerCase();
  void fetch(`${getApiBaseUrl()}${Endpoints.AB_EXPERIMENT_MARK_SEEN}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      token,
    },
    body: JSON.stringify({
      experimentKey: key,
      variantKey: variant,
      osType,
    }),
    keepalive: true,
  }).catch(() => undefined);
}

function clearSeenKeys() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const storage = window.sessionStorage;
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith(SEEN_PREFIX)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => storage.removeItem(key));
  } catch {
    // ignore
  }
}
